---
title: "A sourcing email didn't send"
description: "A sourcing outreach email in Chosen didn't go out. The usual causes — Gmail disconnected, email not enriched, domain rate limits, a reply — and the fixes."
category: "troubleshooting"
keywords:
  - "outreach not sending"
  - "sourcing email didn't send"
  - "outreach email stuck"
  - "outreach queued"
  - "fix outreach sending"
schema: "HowTo"
nav:
  section: "troubleshooting"
  order: 30
  parent: "/troubleshooting"
---

You hit send on a prospect outreach email and it didn't go out. Before you resend, know that some of these "failures" are Chosen working as intended — a queued message and a paced send both look like nothing happening, and both will send themselves. Here's how to tell which is which.

## Gmail isn't connected, or its access expired

Outreach sends from your own connected [Gmail](/getting-started/integrations/gmail) — there's no `noreply@` fallback. No Gmail connection means no sending, full stop. Google access tokens also expire, and revoking access from the Google side disconnects Chosen quietly.

**Check it:** open **Settings → Integrations** and look at Gmail. A greyed-out or disconnected Gmail is the most common reason outreach stalls. The composer also shows **Connect Gmail to send** instead of a send button when there's no connection.

**Fix it:**

<Steps>
  <li>Go to **Settings → Integrations**.</li>
  <li>Click **Connect** next to Gmail and sign in with the address you recruit from. It takes about ten seconds.</li>
  <li>Send the outreach again.</li>
</Steps>

For more on why Gmail disconnects and what it affects, see [integration issues](/troubleshooting/integration-issues).

## The email isn't enriched yet, so the message is queued

This one isn't a failure. If a prospect's email isn't confirmed when you compose outreach, the composer shows **Get email & send** instead of **Send**. That queues your message and starts an [enrichment](/guides/sourcing/enrichment) lookup. The moment enrichment resolves an address, the queued message sends itself — no second visit needed.

**Check it:** if you clicked **Get email & send**, the message is queued, not failed. Enrichment finishes in roughly 10 to 60 seconds.

**Fix it:** usually, wait. If enrichment comes back as **No match**, though, there's no address coming and the message can't send — Chosen won't queue a message that could never go out. In that case, fall back to the prospect's LinkedIn or other [source links](/guides/sourcing/find-prospects) on their record.

## A per-domain rate limit is pacing the send

When several prospects work at the same company, Chosen caps how fast it emails that domain — automatically, with no setting to change. The limits are **5 sends per hour** and **50 sends per day** to any single recipient domain. A send that's waiting on this looks delayed, not failed.

**Check it:** if you queued outreach to several people at the same employer, later messages to that domain are being paced. This is [outreach sending](/guides/sourcing/outreach-sending) protecting your domain reputation — emailing five people at one company within a minute can trip that company's spam tooling and flag everything from your address.

**Fix it:** nothing. The remaining messages send themselves as the per-domain window opens up. The pacing is the feature working — let it run.

## The prospect already replied

When a prospect replies, the rest of that campaign for that prospect pauses automatically. Follow-ups only go to people who haven't answered — nobody who already wrote back gets a "just checking in".

**Check it:** if a follow-up didn't send to a specific prospect, check whether they've replied to an earlier message in the campaign.

**Fix it:** nothing to fix — this is working as intended. The prospect replied, so it's time for a real conversation, not another automated touch. When they're ready to move forward, [convert them into a candidate](/guides/sourcing/prospect-to-candidate).

<Callout type="info" title="Reply detection needs Gmail connected">
Chosen pauses follow-ups by *seeing* the reply land in your inbox. If Gmail disconnects mid-campaign, follow-ups can't tell who answered — which is another reason to keep the [Gmail integration](/getting-started/integrations/gmail) connected while a campaign is running.
</Callout>

## Still stuck?

If Gmail is connected, the email is confirmed, no domain limit applies, and the prospect hasn't replied — but the message still won't send — open **Settings → Contact support** with the prospect's name and the campaign or list it belongs to.
